import { Link } from "react-router-dom";

const A = "/brand";

const btnOutline =
  "inline-flex items-center justify-center gap-2 border-2 border-[#1C1917] text-[#1C1917] rounded-[8px] px-6 py-3 text-sm font-medium hover:bg-[#1C1917] hover:text-white transition-colors";
const btnPrimary =
  "inline-flex items-center justify-center gap-2 bg-[#FF5A4F] text-white rounded-[8px] px-7 py-3.5 text-base font-medium hover:bg-[#e8463c] transition-colors";

const mailto = (program) =>
  `mailto:lara@blueprint4joy.com?subject=${encodeURIComponent(program)}`;

const steps = [
  {
    key: "vision-book",
    headline: "You can feel there's something more\u2026 you just can't see it clearly yet.",
    copy: "Before you know where you're going, you need to remember what matters most.",
    button: "Vision Book Workshop",
    link: "/work-with-me/vision-book-workshop",
    image: "visionbook-window.png",
    alt: "Lara standing by a bright window beside a bookshelf, reflecting quietly.",
    objectPosition: "58% 38%",
    imageLeft: false,
  },
  {
    key: "spark-sessions",
    headline: "You know what you want. You just need help staying in motion.",
    copy: "Sometimes the next step isn't bigger. It's simply the next one.",
    button: "Spark Sessions",
    link: "/work-with-me/spark-sessions",
    image: "workwithme-couch-teal-sparksession.jpg",
    alt: "Lara sitting cross-legged on a gray couch in a teal sweater, laptop on her lap and a JOYspot mug nearby.",
    objectPosition: "50% 35%",
    imageLeft: true,
  },
  {
    key: "consistency-corner",
    headline: "You don't need another fresh start.",
    copy: "You need a place to keep returning to what matters.",
    button: "Consistency Corner",
    link: "/work-with-me/consistency-corner",
    image: "consistencycorner-flowers.png",
    alt: "Lara arranging a vase of fresh ranunculus flowers in a bright kitchen.",
    objectPosition: "45% 55%",
    imageLeft: false,
  },
];

function PathwayBand({ s, index }) {
  const imgRight = !s.imageLeft;
  // widest feather is on the INNER edge (facing text), dissolving into white
  const innerSide = imgRight ? "left" : "right";
  const g = (dir) => `linear-gradient(${dir}, rgba(255,255,255,0) 0%, rgba(255,255,255,0.55) 58%, #FFFFFF 100%)`;
  return (
    <div data-testid={`pathway-${s.key}`} className="relative w-full overflow-hidden bg-white reveal">
      {/* Desktop: image is a full-bleed half-band, feathering into white on the inner edge */}
      <div className={`hidden lg:block absolute inset-y-0 ${imgRight ? "right-0" : "left-0"} w-[56%]`}>
        <img
          src={`${A}/${s.image}`}
          alt={s.alt}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition: s.objectPosition }}
        />
        <div className="absolute inset-y-0 z-[1]" style={{ [innerSide]: 0, width: "46%", background: g(`to ${innerSide}`) }} />
        <div className="absolute inset-x-0 top-0 h-16 z-[1]" style={{ background: g("to top") }} />
        <div className="absolute inset-x-0 bottom-0 h-16 z-[1]" style={{ background: g("to bottom") }} />
      </div>

      {/* Mobile: image stacked on top, dissolving into white at the bottom */}
      <div className="lg:hidden relative w-full aspect-[4/3]">
        <img
          src={`${A}/${s.image}`}
          alt={s.alt}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition: s.objectPosition }}
        />
        <div className="absolute inset-x-0 bottom-0 h-20 z-[1]" style={{ background: g("to bottom") }} />
      </div>

      {/* Text — over the clean white side */}
      <div className="relative z-[2] max-w-7xl mx-auto px-6 md:px-10 flex items-center lg:min-h-[min(72vh,620px)]">
        <div className={`w-full lg:w-1/2 py-10 lg:py-0 ${imgRight ? "" : "lg:ml-auto lg:pl-8"}`}>
          <div className="max-w-xl">
            <span className="block text-[11px] font-medium tracking-[0.28em] uppercase text-[#A8A29E] mb-5">
              {`0${index + 1}`}
            </span>
            <h3 className="font-display text-[34px] md:text-[46px] lg:text-[52px] leading-[1.06] tracking-tight text-[#1C1917] mb-5">
              {s.headline}
            </h3>
            <p className="text-lg md:text-xl text-[#57534E] leading-relaxed mb-8 max-w-lg">
              {s.copy}
            </p>
            {s.link ? (
              <Link to={s.link} data-testid={`pathway-btn-${s.key}`} className={btnOutline}>
                {s.button}
              </Link>
            ) : (
              <a href={mailto(s.button)} data-testid={`pathway-btn-${s.key}`} className={btnOutline}>
                {s.button}
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function OffersIntro() {
  return (
    <section id="offerings" data-testid="offers-intro-section" className="relative overflow-hidden bg-[#FFFFFF]">
      {/* Section headline */}
      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-12 md:pt-16 pb-6 md:pb-8 reveal">
        <div className="max-w-5xl">
          <h2 className="font-display text-[52px] sm:text-[68px] lg:text-[84px] leading-[0.98] tracking-tight text-[#1C1917]">
            One Method.<br />
            Different Ways to Begin.
          </h2>
          <p className="text-xl md:text-2xl text-[#57534E] leading-relaxed mt-6 max-w-2xl">
            There isn't one right first step &mdash; only the one that's right for you.
          </p>
        </div>
      </div>

      {/* Three stepping stones — full-bleed half-bands dissolving into the page */}
      {steps.map((s, i) => (
        <PathwayBand key={s.key} s={s} index={i} />
      ))}

      {/* Destination — full-bleed photographic band (larger; the visual destination) */}
      <div className="reveal mt-6 md:mt-10" data-testid="pathway-ten-week">
        {/* Desktop immersive band */}
        <div className="hidden lg:block relative w-full overflow-hidden" style={{ minHeight: "min(82vh, 780px)" }}>
          <img
            src={`${A}/tenweek-fireplace.png`}
            alt="Lara sitting by a warm fireplace in a cozy library, calm and grounded."
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ objectPosition: "52% 42%", transform: "scale(1.05)", transformOrigin: "22% 46%", filter: "brightness(1.18) contrast(1.02) saturate(1.06)" }}
          />
          <div aria-hidden className="absolute inset-x-0 top-0 h-24 z-[1]" style={{ background: "linear-gradient(to bottom, #FFFFFF 0%, transparent 100%)" }} />
          <div
            aria-hidden
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(to right, rgba(28,25,23,0.7) 0%, rgba(28,25,23,0.52) 30%, rgba(28,25,23,0.3) 52%, rgba(28,25,23,0.1) 74%, rgba(28,25,23,0) 92%)",
            }}
          />
          <div className="relative z-[2] max-w-7xl mx-auto px-6 md:px-10 min-h-[inherit] flex items-center">
            <div className="max-w-xl py-16 md:py-24 lg:-ml-2 xl:-ml-4">
              <span className="block text-[11px] font-medium tracking-[0.28em] uppercase text-white/70 mb-6">
                Where it all leads
              </span>
              <h3 className="font-display text-[73px] lg:text-[86px] leading-[1.02] tracking-tight text-white mb-6">
                You're ready to build a life that finally fits.
              </h3>
              <p className="text-2xl text-white/85 leading-relaxed mb-10 max-w-lg">
                This is where clarity becomes momentum &mdash; and momentum becomes lasting change.
              </p>
              <Link to="/believing-happy" data-testid="pathway-btn-ten-week" className={btnPrimary}>
                10-Week Group Experience
              </Link>
            </div>
          </div>
        </div>

        {/* Mobile: photo stacked on top, text below on clean bg (no overlap) */}
        <div className="lg:hidden">
          <div className="relative w-full aspect-[3/4]">
            <img
              src={`${A}/tenweek-fireplace.png`}
              alt="Lara sitting by a warm fireplace in a cozy library, calm and grounded."
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover"
              style={{ objectPosition: "58% 32%", filter: "brightness(1.12) saturate(1.04)" }}
            />
            <div aria-hidden className="absolute inset-x-0 top-0 h-16 z-[1]" style={{ background: "linear-gradient(to bottom, #FFFFFF 0%, transparent 100%)" }} />
            <div aria-hidden className="absolute inset-x-0 bottom-0 h-20 z-[1]" style={{ background: "linear-gradient(to top, #FFFFFF 0%, transparent 100%)" }} />
          </div>
          <div className="px-6 pt-2 pb-14">
            <span className="block text-[11px] font-medium tracking-[0.28em] uppercase text-[#A8A29E] mb-4">
              Where it all leads
            </span>
            <h3 className="font-display text-[46px] leading-[1.04] tracking-tight text-[#1C1917] mb-5">
              You're ready to build a life that finally fits.
            </h3>
            <p className="text-lg text-[#57534E] leading-relaxed mb-8">
              This is where clarity becomes momentum &mdash; and momentum becomes lasting change.
            </p>
            <Link to="/believing-happy" className={btnPrimary}>
              10-Week Group Experience
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
